// src/private/src/pages/app/clients/ClientListPage.jsx
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import api from '@src/api/clients';

export default function ClientListPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const navigate = useNavigate();

  useEffect(() => {
    api.request({ url: 'dashboard/clients/', method: 'GET' })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setClients(sorted);
      })
      .catch((err) => console.error('Failed to load clients:', err))
      .finally(() => setLoading(false));
  }, []);

  const filteredClients = clients.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()) &&
    (statusFilter === 'all' || (statusFilter === 'active' ? c.active : !c.active))
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Clients</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/clients/new/')}
        >
          + Add Client
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" mt={5}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Dashboard</TableCell>
                  <TableCell>Ad Accounts</TableCell>
                  <TableCell>Parent Company</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell>ZIP Count</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {filteredClients.map((client) => {
                  const clientSlug = client.slug;
                  return (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        {client.client_logo_file || client.client_logo_url ? (
                          <img
                            src={client.client_logo_file || client.client_logo_url}
                            alt="Logo"
                            style={{
                              height: 36,
                              width: 36,
                              objectFit: 'contain',
                              borderRadius: 6,
                              backgroundColor: '#fff',
                              padding: 4,
                              border: '1px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box
                            height={36}
                            width={36}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius={6}
                            border="1px solid #e0e0e0"
                            bgcolor="#f5f5f5"
                            fontSize={12}
                            color="#999"
                          >
                            N/A
                          </Box>
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {client.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Link to={`/clients/${clientSlug}/dashboard`}>Dashboard</Link>
                    </TableCell>
                    <TableCell>
                      {client.facebook_accounts?.map((acc) => (
                        <Box key={`fb-${acc.account_id}`}>
                          <a
                            href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${acc.account_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Facebook: {acc.account_name}
                          </a>
                        </Box>
                      ))}
                      {client.tiktok_advertisers?.map((acc) => (
                        <Box key={`tt-${acc.advertiser_id}`}>
                          <a
                            href={`https://ads.tiktok.com/i18n/manage/campaign?aadvid=${acc.advertiser_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            TikTok: {acc.advertiser_name}
                          </a>
                        </Box>
                      ))}
                      {client.google_customers?.map((acc) => (
                        <Box key={`gg-${acc.customer_id}`}>
                          <a
                            href={`https://ads.google.com/aw/overview?ocid=${acc.customer_id}&ascid=${acc.customer_id}&euid=391788443&__u=1069503107&uscid=7044144174&__c=3239473726&authuser=0&__e=2441890378`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Google: {acc.customer_name}
                          </a>
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>{client.parent_company_name ?? '-'}</TableCell>
                    <TableCell>{client.active ? '✅' : '❌'}</TableCell>
                    <TableCell>
                      {client.current_month_budget !== null
                        ? `$${Number(client.current_month_budget).toLocaleString()}`
                        : '$0'}
                    </TableCell>
                    <TableCell>{client.client_zip_vertical_count ?? 0}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        component={Link}
                        to={`/clients/${clientSlug}/`}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
